import { IressText } from '@iress-oss/ids-components';
import './FooterDisclaimer.css';

interface FooterDisclaimerProps {
  text: string;
}

/**
 * Footer disclaimer shown in all states (onboarding, empty, conversation).
 * Displays centered, muted text below the input area.
 *
 * Rendering depends on the configured text shape:
 * - When the text contains `|` delimiters, the parts are grouped onto two
 *   lines — the first line joins all but the last part with a middot (`·`), and
 *   the last part sits on its own second line (the approved two-line layout).
 * - When the text has NO `|`, it is rendered verbatim on a single line, so an
 *   unstructured/legacy configured string (e.g. one that already uses `·`) is
 *   never mangled with a stray separator or an empty second paragraph.
 */
export function FooterDisclaimer({ text }: Readonly<FooterDisclaimerProps>) {
  const parts = text.split('|').map((p) => p.trim()).filter(Boolean);

  // No delimiter → render the configured string as-is on a single line.
  if (parts.length <= 1) {
    return (
      <div className="footer-disclaimer">
        <IressText
          element="p"
          color="muted"
          textAlign="center"
          className="porter-disclaimer"
        >
          {text}
        </IressText>
      </div>
    );
  }

  // Delimited → first line = all-but-last joined with `·`, second line = last.
  const firstLine = parts.slice(0, -1).join(' · ');
  const secondLine = parts.at(-1) ?? '';

  return (
    <div className="footer-disclaimer">
      <IressText
        element="p"
        color="muted"
        textAlign="center"
        className="porter-disclaimer"
      >
        {firstLine}
      </IressText>
      <IressText
        element="p"
        color="muted"
        textAlign="center"
        className="porter-disclaimer"
      >
        {secondLine}
      </IressText>
    </div>
  );
}
